import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

interface BackendState {
    environment: string;
    timestamp: string;
    databases: {
        mongodb: {
            database: string;
            uri: string;
            status: string;
        };
        firebaseAuth: {
            projectId: string;
            isEmulator: boolean;
            emulatorHost: string;
            status: string;
        };
    };
    services: {
        api: {
            port: string;
            corsOrigins: string[];
        };
        emulators: {
            auth: string;
            ui: string;
        };
    };
    clientIP: string;
    hostname: string;
}

const StatePage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'backend' | 'frontend'>('backend');
    const [backendState, setBackendState] = useState<BackendState | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Get Redux state
    const reduxState = useSelector((state: any) => state);

    useEffect(() => {
        const fetchBackendState = async () => {
            try {
                setLoading(true);
                const response = await fetch('http://localhost:3000/state');

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const data = await response.json();

                if (data.success) {
                    setBackendState(data.data);
                } else {
                    throw new Error(data.message || 'Failed to fetch backend state');
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Unknown error occurred');
                console.error('Error fetching backend state:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchBackendState();
    }, []);

    const formatValue = (value: any): string => {
        if (typeof value === 'object') {
            return JSON.stringify(value, null, 2);
        }
        return String(value);
    };

    const renderBackendTab = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2">Loading backend state...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
                    <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Backend State</h3>
                    <p className="text-red-600">{error}</p>
                    <p className="text-sm text-red-500 mt-2">
                        Make sure the backend API is running on localhost:3000
                    </p>
                </div>
            );
        }

        if (!backendState) {
            return (
                <div className="p-6 text-gray-500">
                    No backend state data available
                </div>
            );
        }

        return (
            <div className="space-y-6">
                {/* Environment Info */}
                <div className="bg-white p-6 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-4">Environment</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Environment</label>
                            <p className="mt-1 text-sm text-gray-900">{backendState.environment}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Timestamp</label>
                            <p className="mt-1 text-sm text-gray-900">{new Date(backendState.timestamp).toLocaleString()}</p>
                        </div>
                    </div>
                </div>

                {/* Databases */}
                <div className="bg-white p-6 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-4">Databases</h3>

                    {/* MongoDB */}
                    <div className="mb-6">
                        <h4 className="text-md font-medium text-gray-800 mb-3">MongoDB</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Database</label>
                                <p className="mt-1 text-sm text-gray-900 font-mono">{backendState.databases.mongodb.database}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">URI</label>
                                <p className="mt-1 text-sm text-gray-900 font-mono">{backendState.databases.mongodb.uri}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Status</label>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${backendState.databases.mongodb.status === 'connected'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                    }`}>
                                    {backendState.databases.mongodb.status}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Firebase Auth */}
                    <div>
                        <h4 className="text-md font-medium text-gray-800 mb-3">Firebase Auth</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Project ID</label>
                                <p className="mt-1 text-sm text-gray-900 font-mono">{backendState.databases.firebaseAuth.projectId}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Emulator Mode</label>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${backendState.databases.firebaseAuth.isEmulator
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                                    }`}>
                                    {backendState.databases.firebaseAuth.isEmulator ? 'Emulator' : 'Production'}
                                </span>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Emulator Host</label>
                                <p className="mt-1 text-sm text-gray-900 font-mono">{backendState.databases.firebaseAuth.emulatorHost}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Status</label>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${backendState.databases.firebaseAuth.status === 'connected'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                    }`}>
                                    {backendState.databases.firebaseAuth.status}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Services */}
                <div className="bg-white p-6 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-4">Services</h3>

                    {/* API */}
                    <div className="mb-6">
                        <h4 className="text-md font-medium text-gray-800 mb-3">API</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Port</label>
                                <p className="mt-1 text-sm text-gray-900 font-mono">{backendState.services.api.port}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">CORS Origins</label>
                                <div className="mt-1">
                                    {backendState.services.api.corsOrigins.map((origin, index) => (
                                        <span key={index} className="inline-block bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded mr-1 mb-1 font-mono">
                                            {origin}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Emulators */}
                    <div>
                        <h4 className="text-md font-medium text-gray-800 mb-3">Emulators</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Auth</label>
                                <p className="mt-1 text-sm text-gray-900 font-mono">{backendState.services.emulators.auth}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">UI</label>
                                <p className="mt-1 text-sm text-gray-900 font-mono">{backendState.services.emulators.ui}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Connection Info */}
                <div className="bg-white p-6 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-4">Connection Info</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Client IP</label>
                            <p className="mt-1 text-sm text-gray-900 font-mono">{backendState.clientIP}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Hostname</label>
                            <p className="mt-1 text-sm text-gray-900 font-mono">{backendState.hostname}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderFrontendTab = () => {
        return (
            <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg border">
                    <h3 className="text-lg font-semibold mb-4">Redux State</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <pre className="text-sm text-gray-800 overflow-auto max-h-96">
                            {JSON.stringify(reduxState, null, 2)}
                        </pre>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">System State</h1>
                    <p className="mt-2 text-gray-600">
                        Monitor backend configuration and frontend Redux state
                    </p>
                </div>

                {/* Tabs */}
                <div className="mb-6">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            <button
                                onClick={() => setActiveTab('backend')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'backend'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Backend Configuration
                            </button>
                            <button
                                onClick={() => setActiveTab('frontend')}
                                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'frontend'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                Frontend Redux State
                            </button>
                        </nav>
                    </div>
                </div>

                {/* Tab Content */}
                <div>
                    {activeTab === 'backend' && renderBackendTab()}
                    {activeTab === 'frontend' && renderFrontendTab()}
                </div>
            </div>
        </div>
    );
};

export default StatePage;
